// Pure helpers behind "My contributions": no React, no I/O, so they are tested
// on their own. The stats payload is untrusted: every field is checked here.

export type HistoryStatus = "validated" | "pending" | "rejected";
export type HistoryKind = "audio" | "text" | "phrase_recording" | "phrase_created" | "other";

export interface HistoryEntry {
  /** Stable within one payload, used as a React key. */
  key: string;
  date: Date | null;
  /** Local calendar day, "YYYY-MM-DD", compared with the date filters. */
  day: string | null;
  language: string | null;
  kind: HistoryKind;
  status: HistoryStatus;
  /** Only audios kept in storage carry one; theme answers do not. */
  blobName: string | null;
}

export interface HistoryFilters {
  status: HistoryStatus | "all";
  language: string | "all";
  /** "YYYY-MM-DD", inclusive. */
  from: string;
  to: string;
}

export const EMPTY_FILTERS: HistoryFilters = { status: "all", language: "all", from: "", to: "" };

const VALIDATED = new Set(["accepted", "validated", "approved", "valid", "final", "done"]);
const REJECTED = new Set(["rejected", "refused", "declined", "invalid"]);

/** Same mapping as the server's contribution history, so both agree. */
export function normalizeStatus(status: unknown): HistoryStatus {
  const value = typeof status === "string" ? status.trim().toLowerCase() : "";
  if (VALIDATED.has(value)) return "validated";
  if (REJECTED.has(value)) return "rejected";
  return "pending";
}

const pad = (n: number) => String(n).padStart(2, "0");
export const localDay = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export interface LanguageRow {
  code: string | null;
  sent: number;
  accepted: number;
  rejected: number;
  pending: number;
  /** 0 to 100, accepted out of sent. */
  rate: number;
}

const num = (value: unknown) => {
  const parsed = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

/** Audios per language, most sent first; languages with no audio are dropped. */
export function languageRows(languages: unknown): LanguageRow[] {
  if (!languages || typeof languages !== "object") return [];
  return Object.entries(languages as Record<string, unknown>)
    .map(([code, raw]) => {
      const value = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
      const sent = num(value.audioUploaded);
      const accepted = num(value.audioAccepted);
      return {
        code: code && code !== "unknown" ? code.toLowerCase() : null,
        sent,
        accepted,
        rejected: num(value.audioRejected),
        pending: num(value.audioPending),
        rate: sent > 0 ? Math.min(100, Math.round((accepted / sent) * 100)) : 0,
      };
    })
    .filter((row) => row.sent > 0)
    .sort((a, b) => b.sent - a.sent || (a.code ?? "").localeCompare(b.code ?? ""));
}

export interface MonthPoint {
  /** "YYYY-MM" */
  month: string;
  accepted: number;
  rejected: number;
  pending: number;
}

/** The last `count` months up to `now`, months without data at zero. */
export function lastMonths(flow: unknown, count: number, now: Date = new Date()): MonthPoint[] {
  const byMonth = new Map<string, MonthPoint>();
  if (Array.isArray(flow)) {
    for (const raw of flow) {
      if (!raw || typeof raw !== "object") continue;
      const point = raw as Record<string, unknown>;
      const month = typeof point.period === "string" ? point.period.slice(0, 7) : "";
      if (!/^\d{4}-\d{2}$/.test(month)) continue;
      const prev = byMonth.get(month) ?? { month, accepted: 0, rejected: 0, pending: 0 };
      byMonth.set(month, {
        month,
        accepted: prev.accepted + num(point.accepted),
        rejected: prev.rejected + num(point.rejected),
        pending: prev.pending + num(point.pending),
      });
    }
  }
  const months: MonthPoint[] = [];
  for (let i = count - 1; i >= 0; i -= 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const month = `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
    months.push(byMonth.get(month) ?? { month, accepted: 0, rejected: 0, pending: 0 });
  }
  return months;
}
